import { type ActionEvent, Controller } from "@hotwired/stimulus"

export default class SnackBarController extends Controller<HTMLElement> {
  static classes = ["active"]
  declare readonly activeClass: string

  static targets = ["item", "template"]
  declare readonly itemTargets: HTMLElement[]
  declare readonly templateTarget: HTMLTemplateElement

  static values = { timeout: { type: Number, default: 4000 } }
  declare timeoutValue: number

  private timeoutIds: Map<HTMLElement, number> = new Map()

  disconnect(): void {
    this.timeoutIds.forEach(window.clearTimeout)
    this.timeoutIds.clear()
  }

  async hide({ currentTarget }: ActionEvent): Promise<void> {
    const item = this.itemTargets.find((item) => item.contains(currentTarget as HTMLElement))!
    await this.dismiss(item)
  }

  async show(message: string): Promise<void> {
    // Only show one item at a time
    await Promise.all(this.itemTargets.map(this.dismiss.bind(this)))

    const newItem = this.templateTarget.content.firstElementChild!.cloneNode(true) as HTMLElement
    newItem.querySelector("span")!.insertAdjacentHTML("beforeend", message)
    this.element.appendChild(newItem)

    const timeoutId = window.setTimeout(() => void this.dismiss(newItem), this.timeoutValue)
    this.timeoutIds.set(newItem, timeoutId)
    this.toggleActiveClass(newItem, true)
  }

  private async dismiss(item: HTMLElement): Promise<void> {
    const timeoutId = this.timeoutIds.get(item)

    if (!timeoutId) {
      return
    }

    window.clearTimeout(timeoutId)
    this.timeoutIds.delete(item)

    await new Promise<void>((resolve) => {
      const onTransitionEnd = (): void => {
        item.remove()
        resolve()
      }

      item.addEventListener("transitionend", onTransitionEnd, { once: true })
      this.toggleActiveClass(item, false)
    })
  }

  private toggleActiveClass(item: HTMLElement, active: boolean): void {
    window.requestAnimationFrame(() => item.classList.toggle(this.activeClass, active))
  }
}
